import * as React from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import { Select } from '@workwave-tidal/core/components'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

import {
  entityCampaignId,
  entityLeadSourceId,
  entityTargetPests,
  useGetEntityFormDataOnMount,
} from '@user/components/Settings/EntityFormSettings'
import { CustomFieldList } from './CustomFieldList'
import { OwnerSelect } from '@user/components/Owner'
import { TargetPests } from '@user/components/Formik'
import { ENTITY_TYPES, getTouchedObject } from '@sales-center/common'
import { SideDrawerForm } from '@sales-center/components'
import { ReAssignSalesTeamAlert } from '@user/components/SalesTeam/ReAssignSalesTeam/ReAssignSalesTeamAlert'

import {
  activeCampaigns,
  selectAllCampaigns,
} from '@user/store/campaigns/slice'
import {
  useGetSalesTeamPermissionMessages,
  useGetSalesTeamsByBranch,
  useGetSalesTeamUsers,
} from '@user/store/salesTeam/hooks'
import { selectAllSalesFunnels } from '@user/store/salesFunnel/slice'
import { salesTeamsForCurrentUser } from '@user/store/salesTeam/slice'
import { useSelectFunnelStagesByType } from '@user/store/salesFunnel/hooks'
import { activeSources, selectAllSources } from '@user/store/sources/slice'
import { useGetCrossBranchValidation } from '@user/store/lead/hooks/useGetCrossBranchValidation'

import { ISource } from '@user/store/sources'
import { ICampaign } from '@user/store/campaigns'
import { ISalesTeam } from '@user/store/salesTeam'
import { ISalesFunnel } from '@user/store/salesFunnel'
import { EditAdditionalInfoFormModel } from './typings'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'

interface Props {
  onCancel: () => void
  entityType: ENTITY_TYPES
  salesFunnelInitialId: number | undefined
  entityId: number
}

export function EditAdditionalInfoFormFields(props: Props) {
  const { onCancel, entityType, entityId, salesFunnelInitialId } = props
  const tidalThemeActive = useTidalThemeActive()

  const salesFunnels = useSelector(selectAllSalesFunnels)
  const leadSources = useSelector(selectAllSources)
  const campaigns = useSelector(selectAllCampaigns)
  const { entitySettingsMap } = useGetEntityFormDataOnMount(entityType)
  const {
    setFieldValue,
    submitForm,
    values,
    setTouched,
    errors,
    setValues,
    submitCount,
    isSubmitting,
  } = useFormikContext<EditAdditionalInfoFormModel>()
  const salesTeamsForUser = useSelector(salesTeamsForCurrentUser)
  const salesTeamsByBranch = useGetSalesTeamsByBranch(values.branchId)
  const { execute: loadSalesTeamUsers } = useGetSalesTeamUsers(
    !!values.salesTeamId ? +values.salesTeamId : undefined,
  )

  const salesFunnelStages = useSelectFunnelStagesByType(
    +values.salesFunnelId,
    entityType == ENTITY_TYPES.Lead ? 'Lead' : 'Opportunity',
  )

  const { crossBranchValidation } = useGetCrossBranchValidation(
    entityId,
    entityType,
  )

  const isSalesTeamRestricted = useMemo(() => {
    return (
      entityType === ENTITY_TYPES.Opportunity ||
      (crossBranchValidation &&
        (crossBranchValidation.hasServices ||
          crossBranchValidation.isMatched ||
          crossBranchValidation.isClosed))
    )
  }, [entityType, crossBranchValidation])

  const salesTeams = useMemo(
    () =>
      isSalesTeamRestricted && !values.removeServices
        ? salesTeamsByBranch
        : salesTeamsForUser,
    [
      isSalesTeamRestricted,
      salesTeamsByBranch,
      salesTeamsForUser,
      values.removeServices,
    ],
  )

  const { positionBasedAccessText, selectedSalesTeamAccessText } =
    useGetSalesTeamPermissionMessages(
      'success',
      values.salesTeamId,
      values.assigneeId,
    )

  useEffect(() => {
    if (!!values.salesTeamId) loadSalesTeamUsers(+values.salesTeamId)
  }, [loadSalesTeamUsers, values.salesTeamId])

  const [sourceRequired] = entitySettingsMap.get(entityLeadSourceId) ?? [false]
  const activeLeadSources = useMemo(() => {
    return activeSources(leadSources, sourceRequired)
  }, [leadSources, sourceRequired])

  const [campaignRequired] = entitySettingsMap.get(entityCampaignId) ?? [false]

  const activeLeadCampaigns = useMemo(() => {
    return activeCampaigns(campaigns, campaignRequired)
  }, [campaigns, campaignRequired])

  const [requiredTargetPests] = entitySettingsMap.get(entityTargetPests) ?? [
    false,
    true,
  ]
  const [requiredCampaignId] = entitySettingsMap.get(entityCampaignId) ?? [
    false,
    true,
  ]
  const [requiredLeadSourceId] = entitySettingsMap.get(entityLeadSourceId) ?? [
    false,
    true,
  ]

  const onChangeSalesTeam = useCallback(
    (value: ISalesTeam | null) => {
      if (value) {
        setValues((v) => ({
          ...v,
          salesTeamId: value?.id,
          assigneeId: 'unassigned',
        }))
      }
      if (!value) {
        setValues((v) => ({
          ...v,
          salesTeamId: 0,
          assigneeId: null,
        }))
      }
    },
    [setValues],
  )

  const onChangeSalesFunnel = useCallback(
    (value: ISalesFunnel | null) => {
      if (value) {
        setFieldValue('salesFunnelId', value.id)
      }
      if (!value) {
        setFieldValue('salesFunnelId', '')
      }
    },
    [setFieldValue],
  )

  const onChangeCampaign = useCallback(
    (value: ICampaign | null) => {
      if (value) {
        const sourceItem = activeLeadSources.find(
          (x) => x.name === value?.source,
        )

        if (sourceItem) {
          setValues((v) => ({
            ...v,
            leadSourceId: sourceItem.id,
            leadSource: sourceItem.name,
            campaignId: value.id,
            campaign: value.name,
          }))
        }
        if (!sourceItem) {
          setValues((v) => ({
            ...v,
            leadSourceId: '',
            leadSource: '',
            campaignId: value.id,
            campaign: value.name,
          }))
        }
      }

      if (!value) {
        setValues((v) => ({
          ...v,
          campaignId: '',
          campaign: '',
          leadSourceId: '',
          leadSource: '',
        }))
      }
    },
    [setValues, activeLeadSources],
  )

  const onLeadSourceChange = useCallback(
    (value: ISource | null) => {
      if (value) {
        setValues((v) => ({
          ...v,
          leadSourceId: value.id,
          leadSource: value.name,
        }))
      }
      if (!value) {
        setValues((v) => ({
          ...v,
          leadSourceId: '',
          leadSource: '',
        }))
      }
    },
    [setValues],
  )

  const onSave = useCallback(() => {
    if (values.leadSourceId) {
      const sourceItem = activeLeadSources.find(
        (x) => x.id === values.leadSourceId,
      )

      setFieldValue('leadSource', sourceItem?.name)
    }

    if (
      salesFunnelInitialId != values.salesFunnelId &&
      salesFunnelStages &&
      salesFunnelStages.length > 0
    ) {
      setFieldValue('salesFunnelStageId', salesFunnelStages[0].id)
    }

    submitForm()
    setTouched(getTouchedObject(errors))
  }, [
    submitForm,
    setFieldValue,
    activeLeadSources,
    values.leadSourceId,
    salesFunnelStages,
    salesFunnelInitialId,
    values.salesFunnelId,
    errors,
    setTouched,
  ])

  const onRemoveServicesChange = useCallback(
    (checked: boolean) => {
      setValues((v) => ({
        ...v,
        removeServices: checked,
        assigneeId: '',
        salesTeamId: 0,
      }))
    },
    [setValues],
  )

  const showSalesTeamAlert = isSalesTeamRestricted

  const height = tidalThemeActive ? '65px' : '54px'
  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  const currentValue = useMemo(
    () => salesTeams?.find((row) => row?.id == values.salesTeamId),
    [salesTeams, values.salesTeamId],
  )

  const isSalesTeamIdValidationError = submitCount > 0 && errors?.salesTeamId

  const salesFunnelValue = useMemo(
    () =>
      salesFunnels?.find(
        (row) => row?.id?.toString() == values.salesFunnelId?.toString(),
      ),
    [salesFunnels, values.salesFunnelId],
  )

  const isSalesFunnelValidationError = submitCount > 0 && errors?.salesFunnelId

  const campaignValue = useMemo(
    () => activeLeadCampaigns?.find((row) => row?.id == values.campaignId),
    [activeLeadCampaigns, values.campaignId],
  )

  const isCampaignValidationError = submitCount > 0 && errors?.campaignId

  const leadSourceValue = useMemo(
    () => activeLeadSources?.find((row) => row?.id == values.leadSourceId),
    [activeLeadSources, values.leadSourceId],
  )

  const isLeadSourceValidationError = submitCount > 0 && errors?.leadSourceId

  return (
    <SideDrawerForm
      onCancel={onCancel}
      onSave={onSave}
      disableSave={isSubmitting}
      disableCancel={isSubmitting}
      primaryBtnLabel="Save"
      saveBtnDataTestId="saveEditAddInfoBtn"
    >
      <Box mr={2} ml={2}>
        {showSalesTeamAlert && (
          <ReAssignSalesTeamAlert
            crossBranchValidation={crossBranchValidation}
            entityType={entityType}
            branch={salesTeamsByBranch[0].branchName}
            onChange={onRemoveServicesChange}
          ></ReAssignSalesTeamAlert>
        )}
      </Box>
      <Box p={2} pt={showSalesTeamAlert ? 0 : 2}>
        <Box mt={1}>
          <Select<ISalesTeam | undefined>
            items={salesTeams}
            onChange={onChangeSalesTeam}
            value={currentValue}
            required
            getLabel={(option) => `${option?.name} - ${option?.branchName}`}
            label={'Sales Team'}
            disableClearOnBlur
            testId="reassignSalesTeamFormSalesTeam"
            data-test-id="reassignSalesTeamFormSalesTeam"
            sx={{ height: 6 }}
            listMode="normal"
            optionMode={'custom'}
            disabled={isSubmitting}
            error={isSalesTeamIdValidationError ? true : false}
            renderOption={(props, team: ISalesTeam) => (
              <MenuItem
                sx={{
                  height: height,
                  minHeight: height,
                }}
                {...props}
                key={team.id}
                value={team.id}
              >
                <Stack>
                  <Typography variant="body1">{team?.name}</Typography>
                  <Typography variant="body2">{team?.branchName}</Typography>
                </Stack>
              </MenuItem>
            )}
          />
          {currentValue == null &&
            !(selectedSalesTeamAccessText || isSalesTeamIdValidationError) && (
              <FormHelperText sx={{ ml: 1 }}>
                Select a Sales Team to see the list of Owners.
              </FormHelperText>
            )}
          {selectedSalesTeamAccessText && (
            <FormHelperText sx={{ ml: 1 }} error>
              {selectedSalesTeamAccessText}
            </FormHelperText>
          )}
          {isSalesTeamIdValidationError && (
            <FormHelperText sx={{ ml: 1 }} error>
              {errors?.salesTeamId}
            </FormHelperText>
          )}
        </Box>

        <OwnerSelect
          entityType={entityType}
          salesTeamId={`${values.salesTeamId}`}
          name="assigneeId"
          useAdditionalMargin={true}
          errorText={positionBasedAccessText}
          testId="additionalInfoDrawerForm"
          required
          disabled={isSubmitting}
        />

        <Box mt={2}>
          <Select<ISalesFunnel | undefined>
            items={salesFunnels}
            onChange={onChangeSalesFunnel}
            value={salesFunnelValue}
            required
            getLabel={(option) => {
              if (option) {
                return `${option.isDefault ? '(Default) ' : ''} ${option.name}`
              }
              return ''
            }}
            label={'Sales Funnel'}
            disableClearOnBlur
            testId="campaignSelect"
            data-test-id="campaignSelect"
            listMode="normal"
            optionMode={'normal'}
            density={density}
            disabled={isSubmitting}
            error={isSalesFunnelValidationError ? true : false}
          />
          {isSalesFunnelValidationError && (
            <FormHelperText sx={{ ml: 1 }} error>
              {errors?.salesFunnelId}
            </FormHelperText>
          )}
        </Box>

        <Box mt={2}>
          <TargetPests
            size="small"
            margin="none"
            required={requiredTargetPests}
            textFieldProps={{ disabled: isSubmitting }}
          />
        </Box>

        <Box mt={2}>
          <Select<ICampaign | undefined>
            items={activeLeadCampaigns}
            onChange={onChangeCampaign}
            value={campaignValue}
            required={requiredCampaignId}
            getLabel={(option) => {
              if (option) {
                return option.id === '0'
                  ? option.name
                  : `${option.name} - ${option.description}`
              }
              return ''
            }}
            label={'Campaign'}
            disableClearOnBlur
            testId="campaignSelect"
            data-test-id="campaignSelect"
            listMode="normal"
            optionMode={'normal'}
            density={density}
            disabled={isSubmitting}
            error={isCampaignValidationError ? true : false}
          />
          {isCampaignValidationError && (
            <FormHelperText sx={{ ml: 1 }} error>
              {errors?.campaignId}
            </FormHelperText>
          )}
        </Box>

        <Box mt={2}>
          <Select<ISource | undefined>
            items={activeLeadSources}
            onChange={onLeadSourceChange}
            value={leadSourceValue}
            required={requiredLeadSourceId}
            getLabel={(option) => {
              if (option) {
                return option.id === '0'
                  ? option.name
                  : `${option.name} - ${option.description}`
              }
              return ''
            }}
            label={'Lead Source'}
            disableClearOnBlur
            testId="leadSourceSelect"
            data-test-id="leadSourceSelect"
            listMode="normal"
            optionMode={'normal'}
            density={density}
            disabled={isSubmitting}
            error={isLeadSourceValidationError ? true : false}
          />
          {isLeadSourceValidationError && (
            <FormHelperText sx={{ ml: 1 }} error>
              {errors?.leadSourceId}
            </FormHelperText>
          )}
        </Box>

        <CustomFieldList />
      </Box>
    </SideDrawerForm>
  )
}
